/**
 * Responsive variants for the two image CDNs the site pulls from.
 *
 * Both resize from query parameters — Supabase uses `width`/`height`, the
 * Pexels CDN uses `w`/`h` — so a narrower variant of any image is just the same
 * URL with those two numbers scaled. Deriving the srcset from the URL keeps one
 * source of truth: services, blog posts and locations carry a single `image`
 * string exactly as before, and nothing has to be kept in sync by hand.
 *
 * Without this the site shipped one 1600px or 1920px file to every device. On a
 * 412px-wide phone that was measured at ~860 KiB of pixels thrown away per page.
 */

/** Candidate widths for full-bleed heroes, which span the viewport. */
export const HERO_WIDTHS = [640, 828, 1080, 1440, 1920] as const;

/** Candidate widths for card artwork, which never exceeds about 640 CSS px. */
export const CARD_WIDTHS = [320, 480, 640, 828, 1200, 1600] as const;

/** Heroes are always full-bleed. */
export const HERO_SIZES = "100vw";

/** Two-column service cards inside the 1280px container. */
export const CARD_SIZES = "(min-width: 1024px) 620px, (min-width: 768px) 50vw, 100vw";

/** Three-column blog cards inside the same container. */
export const BLOG_CARD_SIZES = "(min-width: 1024px) 410px, (min-width: 640px) 50vw, 100vw";

/** Query-parameter names for width and height, by CDN. */
const PARAM_PAIRS = [
  ["width", "height"],
  ["w", "h"],
] as const;

/**
 * Rewrites `url` to a list of width variants, as a `srcset` value.
 *
 * Returns an empty string when the URL carries no recognised size parameters,
 * so a caller can pass it straight to `srcSet` and get the plain `src`
 * behaviour back for anything this cannot resize (a local PNG, say).
 */
export const srcSetFor = (url: string, widths: readonly number[] = CARD_WIDTHS): string => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return "";
  }

  const pair = PARAM_PAIRS.find(([w]) => parsed.searchParams.has(w));
  if (!pair) return "";

  const [widthKey, heightKey] = pair;
  const baseWidth = Number(parsed.searchParams.get(widthKey));
  const baseHeight = Number(parsed.searchParams.get(heightKey));
  if (!Number.isFinite(baseWidth) || baseWidth <= 0) return "";

  // Never offer a variant larger than the original request: upscaling costs
  // bytes and gains nothing.
  const candidates = widths.filter((w) => w <= baseWidth);
  if (candidates.length === 0) return "";

  const ratio = Number.isFinite(baseHeight) && baseHeight > 0 ? baseHeight / baseWidth : 0;

  return candidates
    .map((w) => {
      const variant = new URL(parsed.href);
      variant.searchParams.set(widthKey, String(w));
      if (ratio) variant.searchParams.set(heightKey, String(Math.round(w * ratio)));
      return `${variant.href} ${w}w`;
    })
    .join(", ");
};

/** Intrinsic dimensions declared on an `<img>` so the box is reserved before load. */
export const dimensionsFor = (url: string): { width: number; height: number } | null => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const pair = PARAM_PAIRS.find(([w]) => parsed.searchParams.has(w));
  if (!pair) return null;
  const width = Number(parsed.searchParams.get(pair[0]));
  const height = Number(parsed.searchParams.get(pair[1]));
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
  return { width, height };
};
